import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { PMDialog } from "../../utils/PMDialog";

export default function DocumentationExportDialog({
  open,
  onClose,
  resourceTitle,
  showSignatureOption = false,
  defaultIncludeSignature = false,
  sendLog = [],
  onExport,
}) {
  const { t } = useTranslation();
  const entries = Array.isArray(sendLog) ? sendLog : [];
  const hasSendLog = entries.length > 0;
  const [includeSignature, setIncludeSignature] = useState(defaultIncludeSignature);
  const [includeSendLog, setIncludeSendLog] = useState(hasSendLog);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setIncludeSignature(defaultIncludeSignature);
    setIncludeSendLog(hasSendLog);
  }, [open, defaultIncludeSignature, hasSendLog]);

  const download = async ({ send_log_only = false } = {}) => {
    if (send_log_only && !hasSendLog) return;
    setBusy(true);
    try {
      const response = await onExport?.({
        signature: includeSignature,
        send_log: send_log_only ? true : includeSendLog,
        send_log_only,
      });
      if (response?.errors?.length) {
        throw new Error(response.errors.join(", "));
      }
      if (isPdfBlob(response?.blob)) {
        const url = window.URL.createObjectURL(response.blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = response.filename || "dokument.pdf";
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        onClose?.();
        return;
      }
      if (response?.pdf_url) {
        window.open(response.pdf_url, "_blank", "noopener");
        onClose?.();
        return;
      }
      throw new Error(t("documentationSend.export_failed"));
    } catch (error) {
      toast.error(error?.message || t("documentationSend.export_failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <PMDialog
      open={open}
      onClose={onClose}
      title={t("documentationSend.export_title")}
      size="lg"
      zIndex={3100}
      closeOnOutsideClick
    >
      <p className="mb-4 text-sm text-slate-600">
        {t("documentationSend.export_intro", { title: resourceTitle || "" })}
      </p>

      <div className="space-y-4">
        {showSignatureOption ? (
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={includeSignature}
              onChange={(event) => setIncludeSignature(event.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            {t("documentationSend.export_include_signature")}
          </label>
        ) : null}

        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={includeSendLog}
            onChange={(event) => setIncludeSendLog(event.target.checked)}
            disabled={!hasSendLog}
            className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 disabled:opacity-50"
          />
          {t("documentationSend.export_include_send_log")}
        </label>
        {!hasSendLog ? (
          <p className="text-xs text-slate-500">{t("documentationSend.export_empty_log")}</p>
        ) : null}

        <div className="flex flex-wrap justify-end gap-2 border-t border-slate-100 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            {t("common.cancel")}
          </button>
          <button
            type="button"
            onClick={() => download({ send_log_only: true })}
            disabled={busy || !hasSendLog}
            className="rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
          >
            {t("documentationSend.export_send_log_only")}
          </button>
          <button
            type="button"
            onClick={() => download()}
            disabled={busy}
            className="rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-slate-800 disabled:opacity-60"
          >
            {busy ? t("documentationSend.exporting") : t("documentationSend.export_download")}
          </button>
        </div>
      </div>
    </PMDialog>
  );
}

function isPdfBlob(value) {
  if (!value || typeof value !== "object") return false;
  if (typeof Blob !== "undefined") return value instanceof Blob;
  return typeof value.slice === "function";
}
